var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
/**
 * CZNA-499 — reads the packaged-default drift surface.
 *
 * Two hooks over three endpoints, because they answer two different questions:
 *
 * - `useFormConfigDrift()` — "is anything stale?", asked on every visit to the
 *   forms settings screen. One request for the whole module, not one per
 *   template: the badge has to be right for the template the admin has NOT
 *   selected yet, otherwise they only learn about a stale form by happening to
 *   click it.
 * - `useFormConfigDriftDiff(key, enabled)` — the three-way comparison, fetched
 *   only when the compare dialog opens. It carries three whole form-config
 *   documents, which is not something to prefetch for a badge.
 *
 * MEMORY rule: `useOkapiKy()` returns an unstable reference. It is captured in
 * the query closure and NEVER listed in a dep array — listing it is an infinite
 * refetch loop.
 */
import { useOkapiKy } from '@folio/stripes/core';
import { useQuery } from 'react-query';
import { API_PATHS, CZNA_MODULE_SETTING_DEFAULT_DRIFT } from '../types/apiPaths';
export var formConfigDriftQueryKey = function () { return ['form-config-drift']; };
export var formConfigDriftDiffQueryKey = function (key) {
    return ['form-config-drift-diff', key];
};
/**
 * A drift-list failure is deliberately NOT surfaced as an error banner by the
 * callers: the list is an advisory overlay on a screen that works without it.
 * `isError` is exposed so a caller that wants to say something can, but the
 * default is that a missing badge degrades to today's behaviour rather than
 * blocking the editor behind an error.
 */
var useFormConfigDrift = function () {
    var _a;
    // Closure-capture only. MEMORY: ky is unstable — never in deps.
    var ky = useOkapiKy();
    var _b = useQuery(formConfigDriftQueryKey(), function () { return __awaiter(void 0, void 0, void 0, function () {
        return __generator(this, function (_a) {
            return [2 /*return*/, ky
                    .get(API_PATHS.CZNA_MODULE_SETTINGS_DEFAULT_DRIFT)
                    .json()];
        });
    }); }, {
        staleTime: 0,
        refetchOnWindowFocus: true,
        retry: false,
    }), data = _b.data, isLoading = _b.isLoading, isError = _b.isError;
    var items = (_a = data === null || data === void 0 ? void 0 : data.items) !== null && _a !== void 0 ? _a : [];
    return {
        items: items,
        driftFor: function (settingKey) { var _a; return (_a = items.find(function (i) { return i.key === settingKey; })) !== null && _a !== void 0 ? _a : null; },
        driftCountForRecordType: function (recordType) {
            return items.filter(function (i) { return i.recordType === recordType; }).length;
        },
        isLoading: isLoading,
        isError: isError,
    };
};
export var useFormConfigDriftDiff = function (settingKey, enabled) {
    var _a, _b;
    var ky = useOkapiKy();
    var _c = useQuery(formConfigDriftDiffQueryKey(settingKey), function () { return __awaiter(void 0, void 0, void 0, function () {
        return __generator(this, function (_a) {
            return [2 /*return*/, ky
                    .get(CZNA_MODULE_SETTING_DEFAULT_DRIFT(settingKey))
                    .json()];
        });
    }); }, {
        enabled: enabled,
        // Always refetched on open: the dialog's optimisticLock has to be the
        // current one or the resolve will 409 on a lock the user never saw.
        staleTime: 0,
        cacheTime: 0,
        retry: false,
    }), data = _c.data, isLoading = _c.isLoading, isError = _c.isError, error = _c.error;
    return {
        diff: data !== null && data !== void 0 ? data : null,
        isLoading: isLoading,
        isError: isError,
        errorStatus: (_b = (_a = error === null || error === void 0 ? void 0 : error.response) === null || _a === void 0 ? void 0 : _a.status) !== null && _b !== void 0 ? _b : null,
    };
};
export default useFormConfigDrift;
